import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useReporte, reporteActions } from '../../context/ReporteContext';
import { useEnviarReporte } from '../../hooks/useEnviarReporte';
import { useCorrelativo } from '../../hooks/useCorrelativo';
import { useMaquinas } from '../../hooks/useMaquinas';
import { useOperadores } from '../../hooks/useOperadores';
import { useFiltroMaquinasUsuario } from '../../hooks/useFiltroMaquinasUsuario';
import { useFiltroOperadoresUsuario } from '../../hooks/useFiltroOperadoresUsuario';
import { useListaItemsChequeo } from '../../hooks/useReferencias';
import { 
  Button, 
  Card,
} from '../../components/ui';
import { useInsumos } from '../../hooks/useInsumos';
import { ReportesService } from '../../services';
import ModalAgregarFaena from '../modales/ModalAgregarFaena';
import ModalAgregarInsumo from '../modales/ModalAgregarInsumo';
import ModalConfirmarEliminar from '../modales/ModalConfirmarEliminar';
import { formatDateTimeSafe } from '../../utils/dateUtils';

// Props para el componente
interface FormularioReporteCompletoProps {
  modoEdicion?: boolean;
  reporteId?: number;
  refetchReporte?: () => Promise<any>;  // 🆕 NUEVO: Función para refrescar data
}

const FormularioReporteCompleto: React.FC<FormularioReporteCompletoProps> = ({ 
  modoEdicion = false, 
  reporteId,
  refetchReporte  // 🆕 NUEVO: Recibir función de refetch
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { usuario } = useAuth();
  const { 
    reporte, 
    dispatch, 
    estaCompleto,
    empresaId,
    nombreEmpresa,
    tieneEmpresaActiva,
    puedeCrearReporte,
    validacionHorometros // 🆕 NUEVA: Validación de horómetros
  } = useReporte();

  // 🚀 NUEVO: Hook para envío atómico
  const { estado: estadoEnvio, enviarReporte, limpiarEstado, reintentar } = useEnviarReporte();
  
  // 🆕 NUEVO: Hook para correlativo automático
  const { siguienteNumero: correlativo, cargando: cargandoCorrelativo } = useCorrelativo();
  
  // 🆕 NUEVO: Hook específico para actualización en modo edición
  const useActualizarReporte = () => {
    const [actualizando, setActualizando] = useState(false);

    const actualizarReporte = async () => {
      if (!reporteId) {
        toast.error('ID de reporte no disponible');
        return;
      }

      setActualizando(true);
      
      try {
        // ✅ CORREGIDO: Usar la función de transformación completa con manejo de KML
        const reporteTransformado = await ReportesService.transformarReporteParaBackend(reporte);
        
        // Usar el servicio de reportes para actualizar
        await ReportesService.actualizarReporte(reporteId, reporteTransformado);
        
        // 🆕 NUEVO: Forzar recarga del reporte para mostrar datos actualizados
        if (refetchReporte) {
          await refetchReporte();
        }
        
        toast.success(t('reports.report_updated'));
        navigate(`/reportes/${reporteId}`);
        
      } catch (error: any) {
        console.error('Error actualizando reporte:', error);
        const mensaje = error?.message || 'Error al actualizar el reporte';
        toast.error(mensaje);
      } finally {
        setActualizando(false);
      }
    };

    return { actualizando, actualizarReporte };
  };

  const { actualizando, actualizarReporte } = useActualizarReporte();
  
  // 🆕 NUEVO: Hooks para cargar datos de formulario
  const { maquinas, cargandoMaquinas, errorMaquinas } = useMaquinas();
  const { operadores, cargandoOperadores, errorOperadores } = useOperadores();
  const { errorInsumos } = useInsumos();
  const { filtrarMaquinas, tieneAccesoCompleto, isLoading: isLoadingFiltro } = useFiltroMaquinasUsuario();
  const { filtrarOperadores, isLoading: isLoadingFiltroOperadores } = useFiltroOperadoresUsuario();
  const { data: itemsChequeoData, isLoading: cargandoItemsChequeo, error: errorItemsChequeo } = useListaItemsChequeo();

  // Filtrar máquinas por permisos de usuario
  const maquinasPermitidas = useMemo(() => {
    if (cargandoMaquinas || !maquinas) {
      return [];
    }
    
    const maquinasFiltradas = filtrarMaquinas(maquinas);
    
    // Log para debugging
    if (!tieneAccesoCompleto) {
      // console.log(`🔍 Máquinas disponibles en formulario: ${maquinasFiltradas.length}/${maquinas.length}`);
    }
    
    return maquinasFiltradas;
  }, [maquinas, filtrarMaquinas, tieneAccesoCompleto, cargandoMaquinas]);

  // Filtrar operadores por permisos de usuario
  const operadoresPermitidos = useMemo(() => {
    if (cargandoOperadores || !operadores) {
      return [];
    }

    const operadoresFiltrados = filtrarOperadores(operadores);

    return operadoresFiltrados;
  }, [operadores, filtrarOperadores, cargandoOperadores]);

  // 🆕 NUEVO: Estados para modales
  const [modalFaenaOpen, setModalFaenaOpen] = useState(false);
  const [modalEliminarOpen, setModalEliminarOpen] = useState(false);
  const [faenaAEliminar, setFaenaAEliminar] = useState<any>(null);
  const [faenaAEditar, setFaenaAEditar] = useState<any>(null);
  const [modalEditarFaenaOpen, setModalEditarFaenaOpen] = useState(false);
  
  // 🆕 NUEVO: Estados para modal de cancelar
  const [modalCancelarOpen, setModalCancelarOpen] = useState(false);
  
  // 🆕 NUEVO: Estados para modales de insumos
  const [modalInsumoOpen, setModalInsumoOpen] = useState(false);
  const [modalEditarInsumoOpen, setModalEditarInsumoOpen] = useState(false);
  const [insumoAEditar, setInsumoAEditar] = useState<any>(null);
  const [insumoAEliminar, setInsumoAEliminar] = useState<any>(null);

  // Estados locales para inputs numéricos (permite coma/punto decimal)
  const [localHorometroInicial, setLocalHorometroInicial] = useState(reporte.horometro_inicial.toString());
  const [localHorometroFinal, setLocalHorometroFinal] = useState(reporte.horometro_final.toString());

  // Sincronizar estados locales cuando cambia el reporte
  useEffect(() => {
    setLocalHorometroInicial(reporte.horometro_inicial.toString());
    setLocalHorometroFinal(reporte.horometro_final.toString());
  }, [reporte.horometro_inicial, reporte.horometro_final]);

  const handleGuardarBorrador = () => {
    toast.success('Borrador guardado en memoria');
  };

  const handleEditarFaena = (faena: any) => {
    setFaenaAEditar(faena);
    setModalEditarFaenaOpen(true);
  };

  const handleEliminarFaena = (faena: any) => {
    setFaenaAEliminar(faena);
    setModalEliminarOpen(true);
  };

  const confirmarEliminarFaena = () => {
    if (faenaAEliminar) {
      dispatch(reporteActions.eliminarFaena({ id_temporal: faenaAEliminar.id_temporal }));
      toast.success(`Faena "${faenaAEliminar.faena_codigo}" eliminada`);
      setModalEliminarOpen(false);
      setFaenaAEliminar(null);
    }
  };

  const cancelarEliminarFaena = () => {
    setModalEliminarOpen(false);
    setFaenaAEliminar(null);
  };
  
  // 🆕 NUEVO: Funciones para manejar insumos
  const handleEditarInsumo = (insumo: any) => {
    setInsumoAEditar(insumo);
    setModalEditarInsumoOpen(true);
  };

  const handleEliminarInsumo = (insumo: any) => {
    setInsumoAEliminar(insumo);
    setModalEliminarOpen(true);
  };

  const confirmarEliminarInsumo = () => {
    if (insumoAEliminar) {
      dispatch(reporteActions.eliminarInsumo({ id_temporal: insumoAEliminar.id_temporal }));
      toast.success(`Insumo "${insumoAEliminar.nombre_insumo}" eliminado`);
      setModalEliminarOpen(false);
      setInsumoAEliminar(null);
    }
  };

  const cancelarEliminarInsumo = () => {
    setModalEliminarOpen(false);
    setInsumoAEliminar(null);
  };

  // 🚀 NUEVO: Manejar envío atómico
  const handleEnviarReporte = async () => {
    if (!tieneEmpresaActiva) {
      toast.error('No hay empresa activa seleccionada');
      return;
    }
    
    if (!puedeCrearReporte) {
      toast.error('No tiene permisos para crear reportes en esta empresa');
      return;
    }
    
    if (!estaCompleto) {
      toast.error('Complete todos los campos obligatorios antes de enviar');
      return;
    }

    if (!empresaId) {
      toast.error('Error: No se pudo obtener el ID de la empresa');
      return;
    }
    
    try {
      // Llamar operación atómica
      await enviarReporte(reporte, empresaId);
      
      // No manejamos éxito aquí, se maneja en useEffect
    } catch (error: any) {
      console.error('❌ Error en handleEnviarReporte:', error);
      // No mostramos toast aquí, se maneja en useEffect
    }
  };

  // 🆕 NUEVO: Effects para manejar estados de envío
  useEffect(() => {
    if (estadoEnvio.completado && estadoEnvio.reporteCreado) {
      // 🆕 CORRECCIÓN: Mostrar el número correcto del reporte creado
      const numeroReporte = estadoEnvio.reporteCreado.numero || 'Sin número';
      toast.success(`✅ Reporte #${numeroReporte} enviado exitosamente`);
      
      // Limpiar estado del reporte
      dispatch(reporteActions.limpiar());
      
      // 🆕 NUEVO: Marcar que se debe refrescar la lista
      localStorage.setItem('refreshReportesList', 'true');
      
      // Navegar a lista de reportes
      navigate('/reportes');
      
      // Limpiar estado de envío después de navegar
      setTimeout(() => {
        limpiarEstado();
      }, 1000);
    }
  }, [estadoEnvio.completado, estadoEnvio.reporteCreado, dispatch, navigate, limpiarEstado]);

  useEffect(() => {
    if (estadoEnvio.error) {
      toast.error(`❌ Error al enviar reporte: ${estadoEnvio.error}`);
    }
  }, [estadoEnvio.error]);

  // 🆕 NUEVO: Cargar items de chequeo obligatorios cuando se selecciona una máquina
  useEffect(() => {
    // Solo cargar items si:
    // 1. Hay una máquina seleccionada
    // 2. Los items ya terminaron de cargar (no están en loading)
    // 3. Hay items de chequeo disponibles
    // 4. No estamos en modo edición (para evitar sobreescribir items ya cargados)

    if (!reporte.maquina_id) {
      // console.log('📋 Items de chequeo: No hay máquina seleccionada');
      return;
    }

    if (cargandoItemsChequeo) {
      // console.log('📋 Items de chequeo: Cargando...');
      return;
    }

    if (errorItemsChequeo) {
      // console.error('📋 Items de chequeo: Error al cargar', errorItemsChequeo);
      return;
    }

    if (!itemsChequeoData || itemsChequeoData.length === 0) {
      // console.log('📋 Items de chequeo: No hay items configurados en el sistema');
      return;
    }

    if (modoEdicion) {
      // console.log('📋 Items de chequeo: Modo edición, no auto-cargar');
      return;
    }

    const itemsObligatorios = itemsChequeoData.filter((item) => item.es_obligatorio);

    if (itemsObligatorios.length === 0) {
      // console.log('📋 Items de chequeo: No hay items obligatorios configurados');
      return;
    }

    // Solo agregar items que no estén ya en la lista
    const itemsYaPresentes = reporte.items_chequeo.map(item => item.item_chequeo_id);
    const itemsNuevos = itemsObligatorios.filter(item => !itemsYaPresentes.includes(item.id));

    if (itemsNuevos.length > 0) {
      // Agregar cada item obligatorio
      itemsNuevos.forEach(item => {
        // console.log(`📋 Agregando item: ${item.descripcion} (ID: ${item.id})`);
        dispatch(reporteActions.agregarItemChequeo({
          id_temporal: `item_${item.id}_${Date.now()}`,
          item_chequeo_id: item.id,
          nombre_item: item.descripcion,
          verificado: false,
          horometro: 0,
          tiempo: 0,
          requiere_atencion: false,
          observaciones: ''
        }));
      });

      toast.success(`✅ Se agregaron ${itemsNuevos.length} items de chequeo obligatorios`);
    }
  }, [reporte.maquina_id, itemsChequeoData, cargandoItemsChequeo, errorItemsChequeo, modoEdicion]); // Omitir dispatch y reporte.items_chequeo de deps para evitar loop

  const handleCancelar = () => {
    setModalCancelarOpen(true);
  };

  const confirmarCancelar = () => {
    dispatch(reporteActions.limpiar());
    navigate('/reportes');
    setModalCancelarOpen(false);
  };

  const cancelarCancelar = () => {
    setModalCancelarOpen(false);
  };

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-4 md:space-y-6 md:p-6">
      {/* Header */}
      <div className="flex flex-col space-y-3 md:flex-row md:items-center md:justify-between md:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('reports.title')}</h1>
          <p className="text-gray-600">
            Reporte {reporte.numero_reporte || 'Nuevo'} • {nombreEmpresa} • 
            Estado: <span className="capitalize font-medium">{reporte.estado_reporte}</span>
            {!tieneEmpresaActiva && <span className="text-red-600 ml-2">⚠️ Sin empresa activa</span>}
          </p>
        </div>
        
        <div className="flex flex-col space-y-2 md:flex-row md:items-center md:space-y-0 md:space-x-3">
          {modoEdicion ? (
            /* Botones para modo edición */
            <>
              <Button
                variant="outline"
                onClick={() => navigate(`/reportes/${reporteId}`)}
                disabled={actualizando}
              >
                {t('common.cancel')}
              </Button>

              <Button
                onClick={actualizarReporte}
                disabled={actualizando || !estaCompleto}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {actualizando ? t('common.saving') : t('common.save')}
              </Button>
            </>
          ) : (
            /* Botones para modo creación */
            <>
          <Button
            variant="outline"
            onClick={handleCancelar}
          >
            {t('common.cancel')}
          </Button>

          <Button
            variant="outline"
            onClick={handleGuardarBorrador}
          >
            {t('common.save')}
          </Button>
          
          <Button
            onClick={handleEnviarReporte}
            disabled={!estaCompleto || !tieneEmpresaActiva || !puedeCrearReporte || estadoEnvio.enviando}
            className={`${
              estaCompleto && tieneEmpresaActiva && puedeCrearReporte && !estadoEnvio.enviando
                ? 'bg-green-600 hover:bg-green-700' 
                : 'bg-gray-400 cursor-not-allowed'
            } text-white`}
          >
            {estadoEnvio.enviando ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('reports.sending_report')} ({estadoEnvio.progreso}%)
              </>
            ) : (
              t('reports.submit_report')
          )}
        </Button>
            </>
          )}
        </div>
      </div>

      {/* Indicador de completitud */}
      {!estaCompleto && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-yellow-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <p className="text-sm text-yellow-700">
              {t('faenas.form_incomplete')}
            </p>
          </div>
        </div>
      )}
      
      {/* 🏢 MULTITENANT: Warning si no tiene empresa o permisos */}
      {!tieneEmpresaActiva && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <p className="text-sm text-red-700">
              No hay empresa activa seleccionada. No puede crear reportes.
            </p>
          </div>
        </div>
      )}
      
      {tieneEmpresaActiva && !puedeCrearReporte && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
            </svg>
            <p className="text-sm text-red-700">
              No tiene permisos para crear reportes en la empresa <strong>{nombreEmpresa}</strong>
            </p>
          </div>
        </div>
      )}

      {/* Mensaje para operadores sobre máquinas filtradas */}
      {usuario?.tipo_usuario === 'operador' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-blue-700">
                Solo puedes crear reportes para las máquinas que tienes asignadas.
              </p>
            </div>
            <div className="text-sm text-blue-600 font-medium">
              {maquinasPermitidas.length} máquina{maquinasPermitidas.length !== 1 ? 's' : ''} disponible{maquinasPermitidas.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      )}

      {isLoadingFiltro && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
            <p className="text-sm text-gray-600">
              Cargando permisos de máquinas...
            </p>
          </div>
        </div>
      )}

      <form className="space-y-6">
        {/* 1. INFORMACIÓN BÁSICA */}
        <Card>
          <Card.Header>
            <h3 className="text-lg font-semibold flex items-center">
              📝 1. {t('reports.title')}
              <span className="ml-2 text-sm text-red-500">* {t('common.required')}</span>
            </h3>
          </Card.Header>
          <Card.Content>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('reports.machine')} *</label>
                <select
                  value={reporte.maquina_id || ''}
                  onChange={(e) => dispatch(reporteActions.actualizarBasico({ 
                    maquina_id: parseInt(e.target.value) || null 
                  }))}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  disabled={cargandoMaquinas || isLoadingFiltro}
                >
                  <option value="">
                    {cargandoMaquinas || isLoadingFiltro ? t('reports.loading_machines') : t('reports.select_machine')}
                  </option>
                  {maquinasPermitidas.map((maquina) => (
                    <option key={maquina.id} value={maquina.id}>
                      {maquina.codigo} - {maquina.descripcion}
                    </option>
                  ))}
                </select>
                {errorMaquinas && (
                  <p className="text-xs text-red-600 mt-1">
                    ❌ {errorMaquinas}
                  </p>
                )}
                {maquinasPermitidas.length === 0 && !cargandoMaquinas && !isLoadingFiltro && (
                  <p className="text-xs text-yellow-600 mt-1">
                    ⚠️ {t('reports.no_machines')}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('reports.operator')}</label>
                <select
                  value={reporte.operador_id || ''}
                  onChange={(e) => dispatch(reporteActions.actualizarBasico({
                    operador_id: parseInt(e.target.value) || null
                  }))}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={cargandoOperadores || isLoadingFiltroOperadores}
                >
                  <option value="">
                    {cargandoOperadores || isLoadingFiltroOperadores ? t('reports.loading_operators') : t('reports.select_operator')}
                  </option>
                  {operadoresPermitidos.map((operador) => (
                    <option key={operador.id} value={operador.id}>
                      {operador.codigo}{operador.nombre_completo ? ` - ${operador.nombre_completo}` : ` - ${operador.descripcion}`}
                    </option>
                  ))}
                </select>
                {errorOperadores && (
                  <p className="text-xs text-red-600 mt-1">
                    ❌ {errorOperadores}
                  </p>
                )}
                {operadoresPermitidos.length === 0 && !cargandoOperadores && !isLoadingFiltroOperadores && (
                  <p className="text-xs text-yellow-600 mt-1">
                    ⚠️ {t('reports.no_operators')}
                  </p>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('reports.date')} *</label>
                <input
                  type="date"
                  value={reporte.fecha_reporte}
                  onChange={(e) => dispatch(reporteActions.actualizarBasico({ 
                    fecha_reporte: e.target.value 
                  }))}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('reports.report_number')}
                  <span className="text-xs text-gray-500 ml-2">
                    ({t('reports.auto_correlative')})
                  </span>
                </label>
                <input
                  type="text"
                  value={
                    reporte.numero_reporte.toLowerCase() === 'e' && correlativo
                      ? correlativo  // 🆕 NUEVO: Mostrar correlativo en el campo
                      : reporte.numero_reporte
                  }
                  onChange={(e) => {
                    const valor = e.target.value;
                    dispatch(reporteActions.actualizarBasico({ 
                      numero_reporte: valor 
                    }));
                  }}
                  placeholder="e (automático) o 123 (manual)"
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {/* 🆕 NUEVO: Preview del correlativo */}
                {reporte.numero_reporte.toLowerCase() === 'e' && (
                  <p className="text-xs text-green-600 mt-1">
                    {cargandoCorrelativo ? (
                      <span className="flex items-center">
                        <svg className="animate-spin h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Obteniendo correlativo...
                      </span>
                    ) : correlativo ? (
                      <span>✨ Se generará correlativo: <strong>{correlativo}</strong></span>
                    ) : (
                      <span>✨ Se generará correlativo automático al enviar</span>
                    )}
                  </p>
                )}
                {reporte.numero_reporte && reporte.numero_reporte.toLowerCase() !== 'e' && (
                  <p className="text-xs text-blue-600 mt-1">
                    📝 Se usará número manual: <strong>{reporte.numero_reporte}</strong>
                  </p>
                )}
              </div>
            </div>
          </Card.Content>
        </Card>

        {/* 2. HORÓMETROS */}
        <Card>
          <Card.Header>
            <h3 className="text-lg font-semibold flex items-center">
              ⏱️ 2. {t('checklist.horometer')}s
              <span className="ml-2 text-sm text-red-500">* {t('common.required')}</span>
            </h3>
          </Card.Header>
          <Card.Content>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('reports.initial_horometer')} *</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={localHorometroInicial}
                  onChange={(e) => {
                    const stringValue = e.target.value;
                    setLocalHorometroInicial(stringValue);
                    const normalized = stringValue.replace(',', '.');
                    const num = parseFloat(normalized);
                    if (!isNaN(num) && num >= 0) {
                      dispatch(reporteActions.actualizarHorometros(num, reporte.horometro_final));
                    } else if (stringValue === '' || stringValue === '0') {
                      dispatch(reporteActions.actualizarHorometros(0, reporte.horometro_final));
                    }
                  }}
                  onBlur={() => {
                    const normalized = localHorometroInicial.replace(',', '.');
                    const num = parseFloat(normalized);
                    setLocalHorometroInicial(isNaN(num) ? '0' : num.toString());
                  }}
                  pattern="[0-9]*[.,]?[0-9]*"
                  placeholder="0.0"
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('reports.final_horometer')} *</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={localHorometroFinal}
                  onChange={(e) => {
                    const stringValue = e.target.value;
                    setLocalHorometroFinal(stringValue);
                    const normalized = stringValue.replace(',', '.');
                    const num = parseFloat(normalized);
                    if (!isNaN(num) && num >= 0) {
                      dispatch(reporteActions.actualizarHorometros(reporte.horometro_inicial, num));
                    } else if (stringValue === '' || stringValue === '0') {
                      dispatch(reporteActions.actualizarHorometros(reporte.horometro_inicial, 0));
                    }
                  }}
                  onBlur={() => {
                    const normalized = localHorometroFinal.replace(',', '.');
                    const num = parseFloat(normalized);
                    setLocalHorometroFinal(isNaN(num) ? '0' : num.toString());
                  }}
                  pattern="[0-9]*[.,]?[0-9]*"
                  placeholder="0.0"
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('reports.worked_hours')}</label>
                <input
                  type="number"
                  value={reporte.horas_trabajadas.toFixed(2)}
                  disabled
                  className="w-full border rounded-lg px-3 py-2 bg-gray-100 text-gray-600"
                />
                <p className="text-xs text-gray-500 mt-1">{t('reports.worked_hours_auto')}</p>
              </div>
            </div>
            
            {/* 🆕 NUEVA: Validación visual de horómetros vs faenas */}
            {!validacionHorometros.esValido && validacionHorometros.mensaje && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-red-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <div>
                    <p className="text-sm text-red-600 font-medium">
                      ⚠️ {validacionHorometros.mensaje}
                    </p>
                    <p className="text-xs text-red-500 mt-1">
                      {t('reports.horometer_diff')}: {validacionHorometros.diferenciaHorometros.toFixed(2)}h |
                      {t('reports.faena_hours_sum')}: {validacionHorometros.sumaHorasFaenas.toFixed(2)}h |
                      {t('reports.difference')}: {validacionHorometros.diferencia.toFixed(2)}h
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* 🆕 NUEVA: Validación exitosa */}
            {validacionHorometros.esValido && reporte.faenas.length > 0 && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="text-sm text-green-600">
                    ✅ {t('reports.horometer_match_detail', { suma: validacionHorometros.sumaHorasFaenas.toFixed(2), total: validacionHorometros.diferenciaHorometros.toFixed(2) })}
                  </p>
                </div>
              </div>
            )}
          </Card.Content>
        </Card>

        {/* 3. FAENAS */}
        <Card>
          <Card.Header>
            <h3 className="text-lg font-semibold flex items-center justify-between">
              📋 3. {t('faenas.title')}
              <Button
                type="button"
                size="sm"
                onClick={() => setModalFaenaOpen(true)}
                disabled={!tieneEmpresaActiva || !puedeCrearReporte}
                className={`${
                  tieneEmpresaActiva && puedeCrearReporte
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-gray-400 cursor-not-allowed text-white'
                }`}
              >
                {t('faenas.add_faena')}
              </Button>
            </h3>
          </Card.Header>
          <Card.Content>
            {reporte.faenas.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>{t('faenas.no_faenas')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {reporte.faenas.map((faena, index) => (
                  <div key={faena.id_temporal} className="border rounded-lg p-3 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p><strong>Faena {index + 1}:</strong> {faena.faena_codigo} - {faena.descripcion}</p>
                        <p className="text-sm text-gray-600 mt-1">
                          🏠 Predio: {faena.predio_codigo} • 
                          🌳 Rodal: {faena.rodal_codigo} • 
                          ⏱️ {faena.horas}h • 
                          📏 {faena.area_trabajada} ha
                        </p>
                        {faena.archivos_kml.length > 0 && (
                          <div className="text-sm text-green-600 space-y-1">
                            <div>🗺️ {faena.archivos_kml.length} archivo(s) KML</div>
                            <div className="pl-4 space-y-1">
                              {/* Solo mostrar área si existe y es > 0 */}
                              {!!(faena.area_kml_total && faena.area_kml_total > 0) && (
                                <div>📏 Área KML: {faena.area_kml_total.toFixed(2)} ha</div>
                              )}
                              {/* Solo mostrar longitud si existe y es > 0 */}
                              {!!(faena.longitud_kml_total && faena.longitud_kml_total > 0) && (
                                <div>📐 Longitud KML: {(faena.longitud_kml_total / 1000).toFixed(2)} km</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 ml-3">
                        <button 
                          className="text-blue-500 hover:text-blue-700 p-1 rounded"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleEditarFaena(faena);
                          }}
                          title="Editar faena"
                          type="button"
                        >
                          ✏️
                        </button>
                        <button 
                          className="text-red-500 hover:text-red-700 p-1 rounded"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleEliminarFaena(faena);
                          }}
                          title="Eliminar faena"
                          type="button"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card.Content>
        </Card>

        {/* 4. INSUMOS */}
        <Card>
          <Card.Header>
            <h3 className="text-lg font-semibold flex items-center justify-between">
              📦 4. {t('supplies.title')}
              <Button
                type="button"
                size="sm"
                onClick={() => setModalInsumoOpen(true)}
                disabled={!tieneEmpresaActiva || !puedeCrearReporte}
                className={`${
                  tieneEmpresaActiva && puedeCrearReporte
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-gray-400 cursor-not-allowed text-white'
                }`}
              >
                {t('supplies.add_supply')}
              </Button>
            </h3>
          </Card.Header>
          <Card.Content>
            {errorInsumos && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">❌ {errorInsumos}</p>
              </div>
            )}
            
            {reporte.insumos.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>{t('supplies.no_supplies')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {reporte.insumos.map((insumo, index) => (
                  <div key={insumo.id_temporal} className="border rounded-lg p-3 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p><strong>Insumo {index + 1}:</strong> {insumo.nombre_insumo}</p>
                        <p className="text-sm text-gray-600 mt-1">
                          📦 Cantidad: {insumo.cantidad} {insumo.unidad}
                          {insumo.costo_unitario && insumo.costo_unitario > 0 && (
                            <span className="ml-4">
                              💰 Costo: ${(insumo.cantidad * insumo.costo_unitario).toLocaleString('es-CL')}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2 ml-3">
                        <button 
                          className="text-blue-500 hover:text-blue-700 p-1 rounded"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleEditarInsumo(insumo);
                          }}
                          title="Editar insumo"
                          type="button"
                        >
                          ✏️
                        </button>
                        <button 
                          className="text-red-500 hover:text-red-700 p-1 rounded"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleEliminarInsumo(insumo);
                          }}
                          title="Eliminar insumo"
                          type="button"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Resumen de costos */}
                {reporte.insumos.some(i => i.costo_unitario && i.costo_unitario > 0) && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-4">
                    <p className="text-sm text-green-700">
                      <strong>{t('supplies.total_cost')}:</strong> ${
                        reporte.insumos
                          .reduce((total, insumo) => {
                            const costo = (insumo.costo_unitario || 0) * insumo.cantidad;
                            return total + costo;
                          }, 0)
                          .toLocaleString('es-CL')
                      }
                    </p>
                  </div>
                )}
              </div>
            )}
          </Card.Content>
        </Card>

        {/* 5. ITEMS DE CHEQUEO */}
        <Card>
          <Card.Header>
            <h3 className="text-lg font-semibold flex items-center justify-between">
              ✅ 5. {t('checklist.title')}
              <span className="text-sm text-gray-600">
                Completados: {reporte.items_chequeo.filter(i => i.verificado).length} / {reporte.items_chequeo.length}
              </span>
            </h3>
          </Card.Header>
          <Card.Content>
            {reporte.items_chequeo.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>{t('checklist.no_items')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {reporte.items_chequeo.map((item) => (
                  <div key={item.id_temporal} className="border rounded-lg p-4 bg-white">
                    {/* Checkbox principal */}
                    <div className="flex items-center justify-between mb-3">
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={item.verificado}
                          onChange={(e) => {
                            dispatch(reporteActions.actualizarItemChequeo(
                              item.id_temporal,
                              { verificado: e.target.checked }
                            ));
                          }}
                          className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        />
                        <span className="font-medium text-base">{item.nombre_item}</span>
                      </label>
                      {item.requiere_atencion && (
                        <span className="text-red-500 text-sm">⚠️ Requiere atención</span>
                      )}
                    </div>

                    {/* Campos adicionales si está verificado */}
                    {item.verificado && (
                      <div className="ml-8 space-y-3 pt-2 border-t border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Horómetro *
                              <span className="text-xs text-gray-500 ml-1">(cuando se chequeó)</span>
                            </label>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={item.horometro || ''}
                              onChange={(e) => {
                                const stringValue = e.target.value;
                                const normalized = stringValue.replace(',', '.');
                                const num = parseFloat(normalized);
                                if (!isNaN(num) && num >= 0) {
                                  dispatch(reporteActions.actualizarItemChequeo(
                                    item.id_temporal,
                                    { horometro: num }
                                  ));
                                } else if (stringValue === '' || stringValue === '0') {
                                  dispatch(reporteActions.actualizarItemChequeo(
                                    item.id_temporal,
                                    { horometro: 0 }
                                  ));
                                }
                              }}
                              pattern="[0-9]*[.,]?[0-9]*"
                              placeholder="0.0"
                              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Tiempo *
                              <span className="text-xs text-gray-500 ml-1">(minutos, 1-480)</span>
                            </label>
                            <input
                              type="number"
                              value={item.tiempo || ''}
                              onChange={(e) => {
                                dispatch(reporteActions.actualizarItemChequeo(
                                  item.id_temporal,
                                  { tiempo: parseInt(e.target.value) || 0 }
                                ));
                              }}
                              min="1"
                              max="480"
                              placeholder="5"
                              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Observaciones
                          </label>
                          <textarea
                            value={item.observaciones || ''}
                            onChange={(e) => {
                              dispatch(reporteActions.actualizarItemChequeo(
                                item.id_temporal,
                                { observaciones: e.target.value }
                              ));
                            }}
                            rows={2}
                            placeholder="Observaciones del chequeo..."
                            className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card.Content>
        </Card>

        {/* 6. ESTADO Y OBSERVACIONES */}
        <Card>
          <Card.Header>
            <h3 className="text-lg font-semibold">🔧 6. {t('reports.machine_status')} & {t('reports.observations')}</h3>
          </Card.Header>
          <Card.Content>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('reports.machine_status')} *</label>
                <select
                  value={reporte.estado_maquina}
                  onChange={(e) => dispatch(reporteActions.actualizarBasico({ 
                    estado_maquina: e.target.value as any 
                  }))}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="OPERATIVA">✅ {t('reports.machine_status_operative')}</option>
                  <option value="FALLA_MENOR">⚠️ {t('reports.machine_status_minor_fault')}</option>
                  <option value="FALLA_MAYOR">🔴 {t('reports.machine_status_major_fault')}</option>
                  <option value="MANTENIMIENTO">🔧 {t('reports.machine_status_maintenance')}</option>
                </select>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">{t('reports.observations')}</label>
                  <textarea
                    value={reporte.observaciones}
                    onChange={(e) => dispatch(reporteActions.actualizarBasico({
                      observaciones: e.target.value
                    }))}
                    rows={4}
                    placeholder={t('checklist.placeholder_observations')}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-y min-h-[80px]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">{t('reports.news')}</label>
                  <textarea
                    value={reporte.novedades}
                    onChange={(e) => dispatch(reporteActions.actualizarBasico({ 
                      novedades: e.target.value 
                    }))}
                    rows={4}
                    placeholder="Novedades o incidentes..."
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-y min-h-[80px]"
                  />
                </div>
              </div>
            </div>
          </Card.Content>
        </Card>
      </form>

      {/* Footer con acciones */}
      <div className="flex items-center justify-between pt-6 border-t border-gray-200">
        <div className="text-sm text-gray-500">
          <p>{t('reports.last_update')}: {formatDateTimeSafe(reporte.updated_at || new Date().toISOString())}</p>
          <p>ID temporal: {reporte.id_temporal}</p>
        </div>
        
        {modoEdicion ? (
          /* Botones para modo edición */
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={() => navigate(`/reportes/${reporteId}`)}
              disabled={actualizando}
            >
              ❌ Cancelar
            </Button>
            
            <Button
              onClick={actualizarReporte}
              disabled={actualizando || !estaCompleto}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {actualizando ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('common.saving')}
                </>
              ) : (
                t('common.save')
              )}
            </Button>
          </div>
        ) : (
          /* Botones para modo creación */
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={handleCancelar}
            >
              ❌ Cancelar
            </Button>
            
            <Button
              variant="outline"
              onClick={handleGuardarBorrador}
            >
              💾 Guardar Borrador
            </Button>
            
            <Button
              onClick={handleEnviarReporte}
              disabled={!estaCompleto || !tieneEmpresaActiva || !puedeCrearReporte || estadoEnvio.enviando}
              className={`${
                estaCompleto && tieneEmpresaActiva && puedeCrearReporte && !estadoEnvio.enviando
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-gray-400 cursor-not-allowed'
              } text-white`}
            >
              {estadoEnvio.enviando ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('reports.sending_report')}
                </>
              ) : (
                t('reports.submit_report')
              )}
            </Button>
          </div>
        )}
      </div>
      
      {/* 🚀 NUEVO: Indicador de envío */}
      {estadoEnvio.enviando && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="mb-4">
                <svg className="animate-spin h-12 w-12 text-blue-600 mx-auto" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('reports.sending_report')}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {estadoEnvio.pasoActual}
              </p>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${estadoEnvio.progreso}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {estadoEnvio.progreso}% completado
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ❤️ NUEVO: Modal de error con opción de reintentar */}
      {estadoEnvio.error && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="mb-4">
                <svg className="h-12 w-12 text-red-600 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Error al Enviar Reporte
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                {estadoEnvio.error}
              </p>
              <div className="flex space-x-3 justify-center">
                <Button
                  variant="outline"
                  onClick={limpiarEstado}
                >
                  Cerrar
                </Button>
                <Button
                  onClick={reintentar}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Reintentar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ModalAgregarFaena 
        isOpen={modalFaenaOpen}
        onClose={() => setModalFaenaOpen(false)}
      />
      
      {/* 🆕 NUEVO: Modal Editar Faena */}
      <ModalAgregarFaena 
        isOpen={modalEditarFaenaOpen}
        onClose={() => {
          setModalEditarFaenaOpen(false);
          setFaenaAEditar(null);
        }}
        faenaParaEditar={faenaAEditar}
        modoEdicion={true}
      />
      
      {/* 🆕 NUEVO: Modales de Insumos */}
      <ModalAgregarInsumo 
        isOpen={modalInsumoOpen}
        onClose={() => setModalInsumoOpen(false)}
      />
      
      <ModalAgregarInsumo 
        isOpen={modalEditarInsumoOpen}
        onClose={() => {
          setModalEditarInsumoOpen(false);
          setInsumoAEditar(null);
        }}
        insumoParaEditar={insumoAEditar}
        modoEdicion={true}
      />
      
      {/* 🆕 NUEVO: Modal Confirmar Cancelar */}
      <ModalConfirmarEliminar
        isOpen={modalCancelarOpen}
        titulo="Cancelar Creación de Reporte"
        mensaje="¿Estás seguro de cancelar la creación del reporte?"
        detalles={[
          'Se perderán todos los datos ingresados',
          'Las faenas configuradas se eliminarán',
          'Los archivos KML subidos se descartarán',
          'Esta acción no se puede deshacer'
        ]}
        onConfirmar={confirmarCancelar}
        onCancelar={cancelarCancelar}
        confirmText="Sí, cancelar"
        cancelText="Continuar editando"
        tipo="warning"
      />
      
      {/* 🆕 NUEVO: Modal Confirmar Eliminar */}
      <ModalConfirmarEliminar
        isOpen={modalEliminarOpen}
        titulo={faenaAEliminar ? "Eliminar Faena" : "Eliminar Insumo"}
        mensaje={faenaAEliminar 
          ? `¿Estás seguro de eliminar la faena "${faenaAEliminar?.faena_codigo}"?`
          : `¿Estás seguro de eliminar el insumo "${insumoAEliminar?.nombre_insumo}"?`
        }
        detalles={faenaAEliminar ? [
          `Descripción: ${faenaAEliminar.descripcion}`,
          `Horas trabajadas: ${faenaAEliminar.horas}h`,
          `Área trabajada: ${faenaAEliminar.area_trabajada} ha`,
          faenaAEliminar.archivos_kml?.length > 0 
            ? `Archivos KML: ${faenaAEliminar.archivos_kml.length} archivo(s)` 
            : 'Sin archivos KML',
          'Esta acción no se puede deshacer'
        ] : insumoAEliminar ? [
          `Cantidad: ${insumoAEliminar.cantidad} ${insumoAEliminar.unidad}`,
          insumoAEliminar.costo_unitario && insumoAEliminar.costo_unitario > 0
            ? `Costo total: ${(insumoAEliminar.cantidad * insumoAEliminar.costo_unitario).toLocaleString('es-CL')}`
            : 'Sin costo definido',
          'Esta acción no se puede deshacer'
        ] : []}
        onConfirmar={faenaAEliminar ? confirmarEliminarFaena : confirmarEliminarInsumo}
        onCancelar={faenaAEliminar ? cancelarEliminarFaena : cancelarEliminarInsumo}
        confirmText="Sí, eliminar"
        cancelText="Cancelar"
        tipo="danger"
      />
    </div>
  );
};

export default FormularioReporteCompleto;
